import { ReactElement, cloneElement } from "react";

import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";

import { AppAction } from "./permissions";
import { usePermission } from "./usePermission";

type WithRoleProps = {
  action: AppAction;
  children: ReactElement;
  /**
   * Tooltip shown when the user lacks permission.
   * Pass `null` to disable the tooltip while still disabling the element.
   */
  tooltip?: string | null;
  /**
   * Extra classes applied to the wrapper span shown when the user lacks
   * permission. Defaults to `"inline-flex"`. Use `"flex"` or `"block"` when
   * the wrapped element needs to stretch to fill its container.
   */
  wrapperClassName?: string;
};

/**
 * Wraps any interactive element, disabling it when the user lacks permission.
 * Optionally shows a tooltip explaining why.
 *
 * Unlike `<IfRole>` which hides elements, `<WithRole>` keeps them visible
 * so users understand the feature exists but isn't available to them.
 *
 * Note: disabled buttons swallow mouse events, so the element is wrapped
 * in a `<span>` to ensure the tooltip still triggers on hover.
 *
 * @example
 * <WithRole action="updateConnection">
 *   <Button>Edit</Button>
 * </WithRole>
 *
 * @example
 * <WithRole action="manageBilling" tooltip="Only admins can manage billing">
 *   <Button>Upgrade Plan</Button>
 * </WithRole>
 */
export function WithRole({
  action,
  children,
  tooltip = "You don't have permission to perform this action",
  wrapperClassName,
}: WithRoleProps) {
  const allowed = usePermission(action);

  if (allowed) {
    return children;
  }

  const disabledElement = cloneElement(children, {
    disabled: true,
    "aria-disabled": true,
  });

  if (tooltip === null) {
    return disabledElement;
  }

  return (
    <Tooltip content={tooltip}>
      {/* Span wrapper is required: disabled elements swallow mouse events,
          which would prevent the tooltip from triggering. */}
      <span
        className={cn("cursor-not-allowed", wrapperClassName ?? "inline-flex")}
      >
        {disabledElement}
      </span>
    </Tooltip>
  );
}
